import React, { useEffect, useMemo, useRef, useState } from "react";
import useOutsideClick from "hooks/useOutsideClick";
import { v4 as uuidv4 } from "uuid";
import TableHeaderCell from "./Components/TableHeaderCell";
import { Column, ExpandedTable, TableDefinitions } from "./constants";
import { expandTableDefinitions } from "./utils";
import { finalize } from "rxjs";
import DataStudioDropdown from "./Components/DataStudioDropdown";
import { queryResults, tableDefinitionsQuery, getStudioViews, saveStudioViews } from "data/dataStudio.data";
import { useStreamedReportDownloads } from "hooks";
import { CustomSelect, FormField, Preloader, SubmitButton } from "components";
import { Button, Form, Modal } from '@themesberg/react-bootstrap';
import { FormFieldTypeConstants } from "interfaces";
import { StringParam, useQueryParams } from "use-query-params";
import { useFormik } from "formik";
import { REMINDER_VALIDATION_SCHEMA } from "validation-schemas";
import { FormContext } from "contexts";

const MyQueries = () => {

  const [isLoadingDataDefinitions, setIsLoadingDataDefinitions] = useState(true);

  const { downloadReport, downloadedRows } = useStreamedReportDownloads({
    url: "/data-studio/download-report"
  })


  // views state
  const [studioViews, setStudioViews] = useState<any>([]);
  const [selectedView, setSelectedView] = useState<any>({});
  const [queryParams, setQueryParams] = useQueryParams({ view: StringParam });
  const [showViewModal, setShowViewModal] = useState(false)

  const [tables, setTables] = useState<ExpandedTable[]>([]);
  const addColumnDropdownRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showAddColumnDropdown, setShowAddColumnDropdown] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState<string>("");
  const [selectedColumns, setSelectedColumns] = useState<Column[]>([]);
  const [rowList, setRowList] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // Position of the dropdown
  const selectedTableColumns = useMemo(
    () => tables.find((table) => table.name === "users")?.columns,
    ["users", tables]
  );

  useEffect(() => {
    getStudioViews().subscribe({
      next: (response) => {
        const studioViews: any = response.data;
        setStudioViews(studioViews)
        if (queryParams.view) {
          const foundView = studioViews.find((view: any) => view.id === queryParams.view)
          if (foundView?.id && foundView.view) {
            setSelectedView({ label: foundView.title, value: foundView.id })
            setSelectedColumns(foundView.view as Column[])
            runQuery(foundView.view as Column[])
          }
        }
      },
      error: (error) => {
        console.log(error);
      },
    });

  }, [])


  useOutsideClick(() => {
    setShowAddColumnDropdown(false);
  }, addColumnDropdownRef);

  const addColumn = (column: Column) => {
    const newColumn =
      column?.type === "edge"
        ? {
          ...column,
          id: uuidv4(),
          child: undefined,
        }
        : {
          ...column,
          id: uuidv4(),
        };
    setSelectedColumns((prevColumns) => [...prevColumns, newColumn]);
    setShowAddColumnDropdown(false);
  };

  const clear = () => {
    setSelectedColumns([]);
    setRowList([]);
    setQueryParams({ view: undefined })
    setSelectedView({})
  };

  const handleDownload = () => {
    if (selectedColumns.length === 0 || isLoading) return;
    const result: any = [];
    const traverse = (node: any, path: any = []) => {
      const newPath = [
        ...path,
        node?.filters?.length
          ? { type: node.type, name: node.name, filters: node?.filters }
          : { type: node.type, name: node.name },
      ];
      if (node.child) {
        traverse(node.child, newPath);
      } else {
        result.push({ path: newPath });
      }
    };
    selectedColumns.forEach((item) => traverse(item));
    downloadReport({
      title: "repport",
      query: {
        columns: result
      },
    })

  };

  const runQuery = (selectedColumns: Column[]) => {

    if (selectedColumns.length === 0 || isLoading) return;

    setIsLoading(true);

    const result: any = [];

    const traverse = (node: any, path: any = []) => {
      const newPath = [
        ...path,
        node?.filters?.length
          ? { type: node.type, name: node.name, filters: node?.filters }
          : { type: node.type, name: node.name },
      ];
      if (node.child) {
        traverse(node.child, newPath);
      } else {
        result.push({ path: newPath });
      }
    };

    selectedColumns.forEach((item) => traverse(item));

    queryResults(result)
      .pipe(
        finalize(() => {
          setIsLoading(false);
        })
      )
      .subscribe({
        next: (response) => {
          setRowList(response.data.data);
        },
        error: (error) => {
          setRowList([]);
          console.log(error);
        },
      });

  }

  const handleSubmit = async () => {
    runQuery(selectedColumns)
  };

  useEffect(() => {
    tableDefinitionsQuery().subscribe({
      next: (response) => {
        const tableDefinitions = response.data.data
          .tableDefinitions as TableDefinitions;
        setTables(expandTableDefinitions(tableDefinitions));
        setIsLoadingDataDefinitions(false);
      },
      error: (error) => {
        console.log(error);
      },
    });
  }, []);

  if (isLoadingDataDefinitions) {
    return <div>Loading....</div>;
  }

  return (
    <div className="w-100 h-100">
      <div
        className="w-100 mt-3"
        style={{ overflowY: "auto", height: "calc(100% - 30px)" }}
      >
        <div className="d-flex justify-content-between align-items-center py-1">

          <div className='w-50' style={{ gap: "12px" }}>
            <Form.Label>Load View</Form.Label>
            <CustomSelect
              isSearchable
              isLoading={isLoading}
              options={studioViews.map((view: any) => ({ label: view.title, value: view.id }))}
              value={selectedView}
              onChange={(option: any) => {
                const currentView = studioViews.find((view: any) => view.id === option.value)
                setSelectedView(option)
                setSelectedColumns(currentView.view as Column[])
                setQueryParams({ view: option.value })
                runQuery(currentView.view as Column[])
              }}
            />

          </div>
          <div className="d-flex" style={{ gap: "12px" }}>
            {
              Boolean(selectedColumns?.length) && (
                <button onClick={() => setShowViewModal(true)} className="btn btn-dark me-1">
                  Save View
                </button>
              )
            }
            <button onClick={handleDownload} className="btn btn-dark me-1">
              Download
            </button>
            <button className="btn btn-dark" onClick={clear}>
              Clear
            </button>
            <button onClick={handleSubmit} className="btn btn-dark me-1">
              {isLoading ? "Querying..." : "Run"}
            </button>
          </div>
        </div>
        {
          (() => {

            if (isLoading) {
              return (
                <div className='h-100vh d-flex justify-content-center align-items-center'>
                  <Preloader isIcon show={isLoading} />
                </div>
              )
            }
            
            return (
              <div
                className="w-100 mt-4 border"
                style={{
                  overflowX: "auto",
                  overflowY: "auto",
                  height: "calc(100% - 120px)",
                  borderColor: "#ccc",
                  borderRadius: "4px",
                }}
              >
                {
                  Boolean(selectedColumns.length && rowList.length) && (
                    <div className="alert alert-success d-flex justify-content-center" role="alert">
                     Note: This table displays only the first 10 rows as a preview. The full dataset will be included when you download the file.
                    </div>
                  )
                }
                <div>
                  <table
                    style={{
                      minWidth: "fit-content",
                      maxWidth: "100%",
                      tableLayout: "auto",
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead className="position-relative">
                      <tr>
                        {selectedColumns.map((column) => (
                          <th
                            className="position-relative border text-start fw-bold text-capitalize p-0 align-top"
                            key={column.id}
                            onMouseLeave={() => setActiveDropdownId("")}
                            style={{
                              minWidth: "300px",
                              maxWidth: "350px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              wordWrap: "break-word",
                              whiteSpace: "nowrap",
                              borderColor: "#ccc",
                              backgroundColor: "#f4f4f4",
                            }}
                          >
                            <TableHeaderCell
                              selectedColumns={selectedColumns}
                              column={column}
                              activeDropdownId={activeDropdownId}
                              dropdownPosition={dropdownPosition}
                              selectedTableColumns={selectedTableColumns}
                              setDropdownPosition={setDropdownPosition}
                              setActiveDropdownId={setActiveDropdownId}
                              setSelectedColumns={setSelectedColumns}
                              setRowList={setRowList}
                            />
                          </th>
                        ))}
                      </tr>
                      <div
                        className="position-absolute"
                        style={{
                          top: "0",
                          left: "100%",
                          width: "135px",
                        }}
                      >
                        <div ref={addColumnDropdownRef}>
                          <button
                            onClick={() =>
                              setShowAddColumnDropdown(!showAddColumnDropdown)
                            }
                            className="btn btn-dark"
                          >
                            Add Column +
                          </button>
                          {selectedTableColumns && showAddColumnDropdown && (
                            <DataStudioDropdown
                              options={selectedTableColumns}
                              onSelect={(item) => {
                                addColumn(item);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </thead>
                    <tbody>
                      {rowList.map((item: any, index: number) => (
                        <tr key={index}>
                          {item.map((column: any, index: number) => (
                            <td
                              className="border p-2 text-start bg-white"
                              style={{
                                minWidth: "250px",
                                maxWidth: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                wordWrap: "break-word",
                                whiteSpace: "nowrap",
                                borderColor: "#ccc",
                              }}
                              key={index}
                              title={column}
                            >
                              {column || "N/A"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })()
        }
      </div>
      <Modal
        centered
        onHide={() => setShowViewModal(false)}
        show={showViewModal}>
        <SaveStudioView
          view={selectedColumns}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          setStudioViews={setStudioViews}
          setShowViewModal={setShowViewModal}
        />
      </Modal>
    </div>
  );
};

export default MyQueries;


const SaveStudioView: React.FunctionComponent<any> = ({
  view,
  selectedView,
  setStudioViews,
  setSelectedView,
  setShowViewModal
}) => {

  const [queryParams, setQueryParams] = useQueryParams({ view: StringParam });
  const [loading, setLoading] = useState(false);

  const studioViewSaveForm = useFormik<{ title: string }>({
    initialValues: {
      title: selectedView?.label,
    },
    validationSchema: REMINDER_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      setLoading(true)
      saveStudioViews(
        queryParams.view as string,
        values.title,
        view
      ).pipe(
        finalize(() => setLoading(false))
      ).subscribe(
        (response) => {
          const studioViews = (response.data as any).views;
          const savedViewId = (response.data as any).savedViewId;
          setStudioViews(studioViews)
          setQueryParams({ view: savedViewId })
          const foundView = studioViews.find((view: any) => view.id === savedViewId)
          if (foundView?.id && foundView.view) {
            setSelectedView({ label: foundView.title, value: foundView.id })
          }

          setShowViewModal(false)
        }
      );
    }
  });

  return (
    <Form onSubmit={studioViewSaveForm.handleSubmit}>
      <Modal.Body>
        <FormContext.Provider value={studioViewSaveForm}>
          <FormField
            label='Title'
            placeholder='Add a title...'
            controlId='studioTitleField'
            type={FormFieldTypeConstants.Textarea}
            controlName='title'
          />

        </FormContext.Provider>
      </Modal.Body>
      <Modal.Footer className="justify-content-end px-4">
        <Button type='button' variant="outline-primary" onClick={() => setShowViewModal(false)}>
          Close
        </Button>

        <SubmitButton
          isSubmitting={loading}
          text='Save'
          submittingText='Saving...'
        />
      </Modal.Footer>
    </Form>
  );
};

